import { type ComponentType, ReactNode } from 'react';

import {
  IconChartLine,
  IconFlag,
  IconGridApps,
  IconGridView,
  IconLab,
  IconMessages,
  type IconProps,
  IconUserCircle,
  IconVideo,
} from '@spotify-internal/encore-web';

import {
  type ResourceLinkIcon,
  useResourceLinkExtensions,
} from '../../extensions';

import styles from './ResourceNameLink.module.css';

const RESOURCE_LINK_ICONS: Record<
  ResourceLinkIcon,
  ComponentType<IconProps>
> = {
  chat: IconMessages,
  entity: IconUserCircle,
  factTable: IconChartLine,
  flag: IconFlag,
  identity: IconUserCircle,
  measurement: IconChartLine,
  metric: IconChartLine,
  project: IconGridApps,
  recording: IconVideo,
  surface: IconGridView,
  workflow: IconLab,
};

export interface ResourceNameLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
  withIcon?: boolean;
}

export const ResourceNameLink = ({
  name,
  text,
  className,
  withIcon = false,
}: ResourceNameLinkProps) => {
  const extensions = useResourceLinkExtensions();
  const extension = extensions.find(candidate =>
    candidate.predicate({ name, text, className }),
  );

  if (extension) {
    const DisplayName = extension.DisplayName;
    const Icon =
      withIcon && extension.icon
        ? RESOURCE_LINK_ICONS[extension.icon]
        : undefined;
    const displayText = DisplayName ? (
      <DisplayName name={name}>{text}</DisplayName>
    ) : (
      text
    );
    const content = Icon ? (
      <span className={styles.content}>
        <Icon className={styles.icon} size="small" title="" />
        <span className={styles.text}>{displayText}</span>
      </span>
    ) : (
      displayText
    );

    return extension.render({
      name,
      text: content,
      className,
    });
  }

  return <strong data-resource-ref={name}>{text}</strong>;
};
